import type {
  OdinDataSender,
  JsonDataWrapper,
  OdinDataRetrievalOptions,
  JsonDataItem,
  DropDownResult,
} from '@myosh/odin-components';
import { api } from '../../../redux/services/api';
import type { IntervalFieldRuleState } from '../../fields/schedule-rules-field/interval-rule-field';
import type { SchedulerRuleFieldState } from '../../fields/schedule-rules-field/field-rule-field';
import { dayValues, monthValues, timeIntervals } from '../../fields/schedule-rules-field/scheduler-rules-utils';
import { getFormWorkflowSteps, getGroups, getUsers } from '../../../utils/common-administration-utils';
import { getHierarchyKeyFilter } from '../../../utils/data-grid-filters';
import type {
  SchedulerRecordPatch,
  SchedulerRule,
  SchedulerRuleField,
  SchedulerRuleScheduler,
} from '../../../@types/scheduler-record';
import { createCaptionObj } from '../../../utils/caption-util';
import { timeZones } from '../../../utils/time-zones';
import { USER_TIME_ZONE } from '../../../utils/date-util';
import { forceAssert } from '../../../utils/type-guards';
import { appDispatch } from '../../../redux/store-accessors';

export const getSchedulerOptions = async (
  subscriber: OdinDataSender<JsonDataWrapper>,
  options: OdinDataRetrievalOptions,
  isExternal?: boolean
) => {
  switch (options?.fieldId?.toString()) {
    case 'targetField':
      await getTargetFields(subscriber, options);
      break;
    case 'moduleFormCaption':
      await getModuleCaptions(subscriber, options);
      break;
    case 'groups':
      await getGroups(subscriber, options, isExternal);
      break;
    case 'users':
      await getUsers(subscriber, options);
      break;
    case 'targetStatus':
      await getFormWorkflowSteps(subscriber, options);
      break;
    default:
      if (options?.customProperties?.entityType === 'HIERARCHY_FIELD') {
        await getHierarchyKeyFilter(subscriber, options);
      } else {
        subscriber.sendData();
      }
  }
};

const getTargetFields = async (subscriber: OdinDataSender<JsonDataWrapper>, options: OdinDataRetrievalOptions) => {
  const formId = options?.customProperties?.moduleFormId as number;
  const moduleId = options?.customProperties?.moduleId as number;

  if (options?.page === 1 && formId) {
    const dispatchResult = appDispatch(api.endpoints.formSettings.initiate(formId));
    const result = await dispatchResult;

    if ('fulfilled' === result.status) {
      const comboboxOptions = result.data.fields
        .flatMap((field) => field.fields ?? field)
        .filter((field) => (field.fieldType as string) === 'PERSONFIELD')
        .map((field) => {
          return {
            value: field.id,
            text: field.caption,
          };
        });
      subscriber.sendData({ data: comboboxOptions, requestId: options.requestId });
    } else {
      subscriber.sendData();
    }

    dispatchResult.unsubscribe();
  } else if (moduleId && options?.page === 1) {
    const dispatchResult = appDispatch(api.endpoints.moduleForms.initiate({ moduleId }));
    const result = await dispatchResult;

    if ('fulfilled' === result.status) {
      const forms = result.data.result?.forms
        ?.flatMap((form) => form.fields ?? [])
        .filter((field) => (field.fieldType as string) === 'PERSONFIELD')
        .map((field) => {
          return {
            value: field.id,
            text: field.caption,
          };
        });
      subscriber.sendData({ data: forms, requestId: options.requestId });
    } else {
      subscriber.sendData();
    }
    dispatchResult.unsubscribe();
  } else {
    subscriber.sendData({ data: [], requestId: options.requestId });
  }
};

const getModuleCaptions = async (subscriber: OdinDataSender<JsonDataWrapper>, options: OdinDataRetrievalOptions) => {
  const moduleId = options?.customProperties?.moduleId as number;
  if (moduleId && options?.page === 1) {
    const dispatchResult = appDispatch(api.endpoints.moduleForms.initiate({ moduleId }));
    const result = await dispatchResult;

    if ('fulfilled' === result.status) {
      let forms = result.data.result?.forms?.map((form) => {
        return {
          value: form.id,
          text: form.caption,
        };
      });

      const filter = (options.fieldFilters?.['text']?.value as string)?.toLowerCase() ?? undefined;

      if (filter && !options?.getAllData && forms) {
        forms = forms.filter((forms) => forms.text.toLowerCase().includes(filter));
      }

      subscriber.sendData({ data: forms, requestId: options.requestId });
    } else {
      subscriber.sendData();
    }
    dispatchResult.unsubscribe();
  } else {
    subscriber.sendData();
  }
};

export function isSchedulerRuleField(rule: SchedulerRule): rule is SchedulerRuleField {
  return (rule as SchedulerRuleField).conditionField !== undefined;
}

export const transformSchedulerPatchData = (data: JsonDataItem) => {
  const patch: SchedulerRecordPatch = { id: Number(data.id) };

  const keys = Object.keys(data);
  for (const dataKey of keys) {
    const dataValue = data[dataKey];
    switch (dataKey) {
      case 'targetStatus':
        patch['targetWorkflowStep'] = { id: Number((dataValue as DropDownResult).value) };
        break;
      case 'users':
      case 'groups':
      case 'targetField':
        patch['userAssignment'] = patch['userAssignment'] || {};
        if (Array.isArray(dataValue)) {
          patch['userAssignment'][dataKey] = forceAssert(
            (dataValue as Array<DropDownResult>).map((item: DropDownResult) => ({
              id: Number(item.value),
            }))
          );
        } else {
          const rawValue = (dataValue as DropDownResult).value;
          const value = { id: rawValue != null ? Number(rawValue) : null };
          patch['userAssignment'][dataKey] = forceAssert(value);
        }
        break;
      case 'caption':
        patch[dataKey] = createCaptionObj(dataValue as string);
        break;
      case 'rules': {
        const transformedRules = (dataValue as Array<IntervalFieldRuleState & SchedulerRuleFieldState>)?.map(
          ({
            id,
            type,
            interval,
            scheduledRecordCreationType,
            month,
            dayOfMonth,
            dayOfWeek,
            timeZone,
            hourOfDay,
            conditionField,
            firstOccurrence,
            repeatEvery,
            ...rule
          }) => {
            if (type === 'periodic-schedule') {
              return {
                ...rule,
                type,
                scheduledRecordCreationType: scheduledRecordCreationType?.value || scheduledRecordCreationType,
                interval: interval && Number(interval),
                month: month?.value || month,
                ...(scheduledRecordCreationType?.displayDayOfMonth
                  ? { dayOfMonth: dayOfMonth && Number(dayOfMonth) }
                  : {}),
                dayOfWeek: dayOfWeek?.value || dayOfWeek,
                timeZone: timeZone?.value || timeZone,
                hourOfDay: hourOfDay && Number(hourOfDay),
                ...(!String(id).includes('new') ? { id } : {}),
              };
            } else {
              return {
                ...rule,
                type,
                conditionField: { id: conditionField.value },
                firstOccurrence: firstOccurrence && Number(firstOccurrence),
                repeatEvery: repeatEvery && Number(repeatEvery),
                ...(!String(id).includes('new') ? { id } : {}),
              };
            }
          }
        );
        patch[dataKey] = forceAssert(transformedRules);
        break;
      }
      default:
        patch[dataKey as keyof SchedulerRecordPatch] = forceAssert(dataValue);
        break;
    }
  }
  return patch;
};

export const transformeSchedulerRule = (rule: JsonDataItem) => {
  if (rule?.type === 'periodic-schedule') {
    return mapPeriodicSchedule(rule as SchedulerRuleScheduler);
  }
  return mapFieldCondition(rule as SchedulerRuleField);
};

export const mapPeriodicSchedule = (item: SchedulerRuleScheduler) => {
  const {
    interval,
    scheduledRecordCreationType,
    month,
    dayOfMonth,
    dayOfWeek,
    timeZone,
    hourOfDay,
    id,
    type,
    ...rest
  } = item;

  return {
    ...rest,
    interval: interval ?? 0,
    scheduledRecordCreationType: scheduledRecordCreationType
      ? timeIntervals.find((i) => i.value === scheduledRecordCreationType)
      : timeIntervals[2],
    month: monthValues.find((i) => i.value === month),
    dayOfMonth: dayOfMonth ?? 1,
    dayOfWeek: dayValues.find((i) => i.value === dayOfWeek),
    timeZone: timeZones.find((i) => (timeZone ? i.value === timeZone : i.value === USER_TIME_ZONE)),
    hourOfDay: hourOfDay ?? 0,
    id,
    type,
  };
};

export const mapFieldCondition = (item: SchedulerRuleField) => {
  const { conditionFieldId, fieldCaption, firstOccurrence, repeatEvery, displayName, id, type, ...rest } = item;

  return {
    ...rest,
    conditionFieldId,
    fieldCaption,
    conditionField: {
      text: fieldCaption?.translations?.[0]?.value ?? '',
      value: conditionFieldId,
    },
    firstOccurrence: firstOccurrence ?? 0,
    repeatEvery: repeatEvery ?? 1,
    id,
    type,
    displayName,
  };
};
